
import React, { useEffect, useMemo, useState } from "react";
import "./DailyPOTracker.css";
import { baseUrl } from "../../shared/Fetch";

interface DailyPO {
    date: string; // YYYY-MM-DD
    achievedPOs: number;
}

interface DailyPOTrackerProps {
    apiUrl: string;
    target?: number;
    refreshInterval?: number;
}

interface ApiResponse {
    data: DailyPO[];
}

const TARGET = 14;

// Thanksgiving holidays
const holidays = [
    `${2026}-11-26`,
    `${2026}-11-27`
];

const isWorkingDay = (date: Date) => {
    const day = date.getDay();

    const dateString = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0")
    ].join("-");

    return (
        day !== 0 &&
        day !== 6 &&
        !holidays.includes(dateString)
    );
};

const getNovemberWorkingDays = () => {
    const dates: string[] = [];

    for (let day = 1; day <= 30; day++) {
        const date = new Date(2026, 10, day);
        const weekday = date.getDay();

        if (isWorkingDay(date)) {
            const formattedDate = `2026-11-${String(day).padStart(2, "0")}`;
            dates.push(formattedDate);
        }
    }

    return dates;
};

const formatDate = (dateString: string) => {
    const [, month, day] = dateString.split("-");

    const date = new Date(
        Number(dateString.split("-")[0]),
        Number(month) - 1,
        Number(day)
    );

    return {
        day: day,
        weekday: date.toLocaleDateString("en-US", {
            weekday: "short",
        }),
    };
};

const DailyPOTracker: React.FC<DailyPOTrackerProps> = ({
    apiUrl,
    target = TARGET,
    refreshInterval = 0,
}) => {
    const [dailyData, setDailyData] = useState<
        Record<string, number>
    >({});

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const workingDays = useMemo(
        () => getNovemberWorkingDays(),
        []
    );

    const fetchDailyPOs = async (signal?: AbortSignal) => {
        try {
            setError("");

            const params = {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(sessionStorage.getItem('auth') + "")?.token}`
                },
                signal
            };

            const response = await fetch((baseUrl + apiUrl), params);

            if (!response.ok) {
                throw new Error("Unable to fetch daily PO data");
            }

            const result: ApiResponse | DailyPO[] =
                await response.json();

            const records = Array.isArray(result)
                ? result
                : result.data;

            const formattedData: Record<string, number> = {};

            records.forEach((item) => {
                if (
                    workingDays.includes(item.date) &&
                    Number.isFinite(Number(item.achievedPOs))
                ) {
                    formattedData[item.date] = Number(item.achievedPOs);
                }
            });

            setDailyData(formattedData);
        } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
                return;
            }

            setError(
                err instanceof Error
                    ? err.message
                    : "Something went wrong"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();

        fetchDailyPOs(controller.signal);

        let interval: ReturnType<typeof setInterval> | undefined;

        if (refreshInterval > 0) {
            interval = setInterval(() => {
                fetchDailyPOs();
            }, refreshInterval);
        }

        return () => {
            controller.abort();

            if (interval) {
                clearInterval(interval);
            }
        };
    }, [apiUrl, refreshInterval]);

    const summary = useMemo(() => {
        let achieved = 0;
        let below = 0;
        let pending = 0;

        workingDays.forEach((date) => {
            if (dailyData[date] === undefined) {
                pending++;
            } else if (dailyData[date] >= target) {
                achieved++;
            } else {
                below++;
            }
        });

        return { achieved, below, pending };
    }, [dailyData, workingDays, target]);

    const getStatus = (date: string) => {
        const value = dailyData[date];

        if (value === undefined) return "pending";

        return value >= target ? "success" : "danger";
    };

    return (
        <div className="daily-po-tracker">

            {/* Header */}
            <div className="tracker-header">
                <div>
                    <div className="tracker-title">
                        <span className="tracker-icon">▦</span>
                        Daily PO Achievement Tracker
                    </div>

                    <div className="tracker-subtitle">
                        November 2026 · Daily target: {target} POs
                    </div>
                </div>

                <div className="tracker-live">
                    <span className="live-dot" />
                    LIVE TRACKING
                </div>
            </div>

            {/* Summary cards */}
            <div className="tracker-summary">

                <div className="summary-card">
                    <span>DAILY TARGET</span>
                    <strong>{target} POs</strong>
                </div>

                <div className="summary-card success-summary">
                    <span>TARGET ACHIEVED</span>
                    <strong>{summary.achieved}</strong>
                </div>

                <div className="summary-card danger-summary">
                    <span>BELOW TARGET</span>
                    <strong>{summary.below}</strong>
                </div>

                <div className="summary-card">
                    <span>PENDING DATA</span>
                    <strong>{summary.pending}</strong>
                </div>

            </div>

            {/* Loading and error */}
            {loading && (
                <div className="tracker-message">
                    Loading daily PO achievements...
                </div>
            )}

            {/* {error && (
                <div className="tracker-error">
                    {error}
                </div>
            )} */}

            {/* Daily tiles */}
            <div className="daily-po-grid">

                {workingDays.map((date) => {
                    const { day, weekday } = formatDate(date);
                    const status = getStatus(date);
                    const achieved = dailyData[date];

                    return (
                        <div
                            key={date}
                            className={`po-day-tile ${status}`}
                        >
                            <div className="po-day-header">
                                <span>{weekday}</span>
                                <span>{day} NOV</span>
                            </div>

                            <div className="flex align-items-center justify-content-between">
                                <div className="po-achievement">
                                    {achieved !== undefined ? achieved : "--"}
                                    <span> / {target}</span>
                                </div>
                                {achieved > target && <div className="ml-5 text-xl">
                                    {achieved - target}<sup>+</sup>🔥
                                </div>}
                            </div>


                            <div className="po-progress-track">
                                <div
                                    className="po-progress-fill"
                                    style={{
                                        width: `${achieved !== undefined
                                            ? Math.min(
                                                (achieved / target) * 100,
                                                100
                                            )
                                            : 0
                                            }%`,
                                    }}
                                />
                            </div>

                            <div className="po-status">
                                {status === "success" && (
                                    <>
                                        <span>✓</span> TARGET HIT
                                    </>
                                )}

                                {status === "danger" && (
                                    <>
                                        <span>!</span> TARGET GAP
                                    </>
                                )}

                                {status === "pending" && (
                                    <>
                                        <span>◷</span> PENDING
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

            </div>

            {/* Legend */}
            <div className="tracker-legend">
                <div>
                    <span className="legend-dot green" />
                    Target achieved (14 or more)
                </div>

                <div>
                    <span className="legend-dot red" />
                    Below target (less than 14)
                </div>

                <div>
                    <span className="legend-dot grey" />
                    Data pending
                </div>
            </div>

        </div>
    );
};

export default DailyPOTracker;
